import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { auth, db } from "../api/firebase";
import { ref, push, set } from "firebase/database";
import {
  loadEcardDraft,
  clearEcardDraft,
  clearEcardAccount,
  loadEcardAccount,
  saveEcardAccount,
  applyCoupon,
  saveLastShare,
} from "../constants/giftJourney";
import { getGiftCardSummaryById } from "../services/giftCardSummaryService";
import { mergeSummaryWithVisualPreset } from "../constants/ecardVisualPresets";
import {
  openRazorpayForProduct,
} from "../services/razorpayCheckout";
import "../styles/productDetails.css";
import "../styles/journey.css";

export default function ProductCheckout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [payErr, setPayErr] = useState("");

  useEffect(() => {
    const d = loadEcardDraft(id);
    if (!d || !d.recipientName) {
      navigate(`/product/${id}/customize`, { replace: true });
      return;
    }
    const account = loadEcardAccount(id);
    const activeEmail = String(auth?.currentUser?.email || "").trim().toLowerCase();
    if (!account?.email && activeEmail) {
      saveEcardAccount(id, { email: activeEmail });
    } else if (!account?.email) {
      navigate(`/product/${id}/account`, { replace: true });
      return;
    }
    setDraft(d);
    if (!db) {
      setLoadErr("Service is temporarily unavailable. Please try again shortly.");
      return;
    }
    let cancelled = false;
    (async () => {
      const summary = await getGiftCardSummaryById(id);
      if (cancelled) return;
      if (!summary) {
        setLoadErr("Gift card not found.");
        return;
      }
      const occ = d.occasionSlug || "egift";
      const merged = mergeSummaryWithVisualPreset(summary, occ);
      setProduct(merged);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const baseRupee = useMemo(() => {
    if (!product) return 0;
    return product.Price - (product.Price * (product.Discount || 0)) / 100;
  }, [product]);

  const couponResult = useMemo(
    () => applyCoupon(baseRupee, coupon),
    [baseRupee, coupon]
  );

  useEffect(() => {
    if (!coupon.trim()) {
      setCouponMsg("");
      return;
    }
    if (couponResult.invalid) {
      setCouponMsg("Code not recognized — try WELCOME10 or FIRST50.");
    } else if (couponResult.discountLabel) {
      setCouponMsg(`Applied: ${couponResult.discountLabel}`);
    } else {
      setCouponMsg("");
    }
  }, [coupon, couponResult]);

  const couponMsgStyle =
    coupon.trim() && couponResult.invalid
      ? { color: "#b91c1c" }
      : { color: "#15803d" };

  const handlePay = async () => {
    if (!product || !draft) return;
    setPayErr("");
    setPayBusy(true);
    try {
      const virtualProduct = {
        ...product,
        Price: couponResult.total,
        Discount: 0,
      };
      const res = await openRazorpayForProduct(virtualProduct, {
        onDismiss: () => {
          setPayErr("Payment was canceled. You can try again when ready.");
        },
      });
      if (!res) {
        setPayBusy(false);
        return;
      }

      let giftKey = null;
      let privateDraftId = null;
      const giftPayload = {
        Tittle: draft.cardTitle || product.Tittle,
        reciever: draft.recipientName,
        Name: draft.senderName,
        Greetings: draft.greetingLine || "",
        Message: draft.mainMessage,
        Image1: product.imageurl,
        image2: product.image2 || null,
        image3: product.image3 || null,
      };

      if (db && auth?.currentUser?.uid) {
        try {
          const uid = auth.currentUser.uid;
          const node = push(ref(db, `users/${uid}/ecard_drafts`));
          privateDraftId = node.key;
          await set(node, {
            uid,
            ownerEmail: String(auth.currentUser.email || "").trim().toLowerCase(),
            gift: giftPayload,
            giftkartCategoryId: draft.giftkartCategoryId,
            status: "private",
            schedule: {
              enabled: Boolean(draft.schedule?.enabled),
              scheduledAt: Number(draft.schedule?.scheduledAt || 0),
              method: String(draft.schedule?.method || "anywhere"),
              emailTo: String(
                draft.schedule?.emailTo || auth.currentUser.email || ""
              )
                .trim()
                .toLowerCase(),
              emailSentAt: null,
              deliveryStatus: "pending",
            },
            createdAt: new Date().toISOString(),
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_order_id: res.razorpay_order_id,
            internalOrderId: res.internalOrderId || null,
          });
        } catch (e) {
          console.error("Draft save failed (check RTDB rules):", e);
          const rawMsg = String(e?.message || "");
          const isPerm =
            String(e?.code || "").includes("PERMISSION_DENIED") ||
            /permission_denied|Permission denied/i.test(rawMsg);
          setPayErr(
            isPerm
              ? "Payment succeeded, but we could not save your card draft. Please login again and retry."
              : `Payment succeeded, but draft save failed: ${rawMsg || "unknown error"}`
          );
          setPayBusy(false);
          return;
        }
      } else {
        setPayErr(
          "Login required before payment. Please login again and retry."
        );
        setPayBusy(false);
        return;
      }

      clearEcardDraft(id);
      clearEcardAccount(id);
      saveLastShare(id, {
        internalOrderId: res.internalOrderId || null,
        privateDraftId,
        giftKey,
        giftkartCategoryId: draft.giftkartCategoryId,
        previewGift: giftPayload,
        schedule: draft.schedule || null,
        hadPublish: Boolean(giftKey),
      });
      navigate(`/product/${id}/sent`, {
        replace: true,
        state: {
          internalOrderId: res.internalOrderId || null,
          privateDraftId,
          previewGift: giftPayload,
          schedule: draft.schedule || null,
          giftKey,
          giftkartCategoryId: draft.giftkartCategoryId,
        },
      });
    } catch (e) {
      setPayErr(e.message || "Payment could not start.");
    } finally {
      setPayBusy(false);
    }
  };

  if (loadErr) {
    return (
      <div className="pd-wrap">
        <div className="pd-error journey-panel">{loadErr}</div>
        <Link to="/" className="journey-link-back">
          Home
        </Link>
      </div>
    );
  }

  if (!product || !draft) {
    return <p className="pd-loading">Preparing checkout…</p>;
  }

  return (
    <div className="pd-wrap">
      <div className="journey-panel journey-checkout">
        <Link to={`/product/${id}/customize`} className="journey-link-back">
          ← Edit message
        </Link>
        <h1>Review &amp; pay</h1>

        <div className="journey-checkout-grid">
          <div className="journey-summary-card">
            <h2>Your card</h2>
            <p>
              <strong>To:</strong> {draft.recipientName}
            </p>
            <p>
              <strong>From:</strong> {draft.senderName}
            </p>
            <p className="journey-summary-msg">{draft.mainMessage}</p>
            <p className="journey-price-line">
              Design: <strong>{product.Tittle}</strong>
            </p>
            <p className="journey-price-line">
              Catalog price:{" "}
              <strong>₹{Math.round(baseRupee)}</strong>
              {product.Discount > 0 && (
                <span className="journey-strike"> ₹{product.Price}</span>
              )}
            </p>
            {product.Validity ? (
              <p className="journey-price-line">
                Validity: <strong>{product.Validity}</strong>
              </p>
            ) : null}
            {couponResult.discountAmount > 0 && (
              <p className="journey-price-line journey-save">
                Coupon savings: −₹{couponResult.discountAmount}
              </p>
            )}
            <p className="journey-total">You pay: ₹{Math.round(couponResult.total * 100) / 100}</p>
          </div>

          <div className="journey-coupon-card">
            <h2>Coupon</h2>
            <p className="journey-hint-small">Enter your coupon code, if you have one.</p>
            <input
              className="journey-coupon-input"
              placeholder="Enter code"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value.toUpperCase())}
            />
            {couponMsg && (
              <p className="journey-coupon-msg" style={couponMsgStyle}>
                {couponMsg}
              </p>
            )}
            <button
              type="button"
              className="pd-btn pd-btn-pay journey-pay-btn"
              disabled={payBusy}
              onClick={handlePay}
            >
              {payBusy ? "Opening Razorpay…" : "Pay securely"}
            </button>
            {payErr && <p className="pd-error">{payErr}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
